import { useMemo } from 'react';
import { LAYOUT_STORAGE_VERSION } from '../../domain';
import type { PersistedLayout, ResolvedColumn, SubTable } from '../../domain';
import type { TableAdapter } from '../../ports';
import { resolveColumns } from '../columns/use-columns';

const EMPTY_LAYOUT: PersistedLayout = { v: LAYOUT_STORAGE_VERSION };

export interface ControlTarget {
    adapter: TableAdapter<unknown>;
    columns: ResolvedColumn<unknown>[];
}

export interface UseControlTargetArgs<T> {
    adapter: TableAdapter<T>;
    columns: ResolvedColumn<T>[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see TableProps.subTable for why `any`, not `unknown`
    subTable?: SubTable<T, any>;
    childAdapter: TableAdapter<unknown> | null;
    expanded: boolean;
}

export function useControlTarget<T>(
    args: UseControlTargetArgs<T>,
): ControlTarget {
    const { adapter, columns, subTable, childAdapter, expanded } = args;

    const childColumns = useMemo(
        () =>
            subTable
                ? resolveColumns({
                      defs: subTable.columns,
                      caps: childAdapter?.getSnapshot().capabilities ?? {},
                      layout: EMPTY_LAYOUT,
                  })
                : [],
        [subTable, childAdapter],
    );

    if (expanded && childAdapter) {
        return { adapter: childAdapter, columns: childColumns };
    }
    return {
        adapter: adapter as unknown as TableAdapter<unknown>,
        columns: columns as unknown as ResolvedColumn<unknown>[],
    };
}
